import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";

import {
  Field,
  getIn,
  useFormikContext,
} from "formik";
import { FieldLabel } from "react-invenio-forms";
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Label,
  Message,
} from "semantic-ui-react";


const getResourceTypeId = (value) => {
  if (typeof value === "string") {
    return value;
  }

  return value?.id ?? value?.value ?? "";
};


const getCreatorLabel = (record) => {
  const creators = record?.metadata?.creators ?? [];

  const names = creators
    .map((creator) => {
      const personOrOrg = creator?.person_or_org ?? {};

      if (personOrOrg.name) {
        return personOrOrg.name;
      }

      return [
        personOrOrg.given_name,
        personOrOrg.family_name,
      ]
        .filter(Boolean)
        .join(" ");
    })
    .filter(Boolean);

  return names.length > 0
    ? names.join("; ")
    : "Unknown creator";
};


const recordToOption = (record) => {
  const title =
    record?.metadata?.title ||
    "Untitled Physical object";

  const creator = getCreatorLabel(record);

  const recordUrl =
    record?.links?.self_html ||
    `${window.location.origin}/records/${record.id}`;

  return {
    key: record.id,
    value: recordUrl,
    text: `${title} — ${creator}`,
  };
};


export const HeritageObjectField = ({
  fieldPath,
  label,
  icon,
  placeholder,
  description,
  openButtonLabel,
}) => {
  const formik = useFormikContext();

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const resourceTypeValue = getIn(
    formik.values,
    "metadata.resource_type"
  );

  const resourceTypeId =
    getResourceTypeId(resourceTypeValue);

  const isPhysicalObject =
    resourceTypeId === "physicalobject";

  const selectedValue =
    getIn(formik.values, fieldPath, "") || "";


  const loadPhysicalObjects = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");

    try {
      const allRecords = [];
      const pageSize = 100;
      let page = 1;
      let total = null;

      while (
        total === null ||
        allRecords.length < total
      ) {
        const params = new URLSearchParams({
          q: "metadata.resource_type.id:physicalobject",
          page: String(page),
          size: String(pageSize),
          sort: "newest",
        });

        const response = await fetch(
          `/api/records?${params.toString()}`,
          {
            credentials: "same-origin",
            headers: {
              Accept:
                "application/vnd.inveniordm.v1+json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Records API returned HTTP ${response.status}`
          );
        }

        const data = await response.json();
        const pageRecords = data?.hits?.hits ?? [];
        const rawTotal = data?.hits?.total;

        total =
          typeof rawTotal === "number"
            ? rawTotal
            : rawTotal?.value ??
              pageRecords.length;

        allRecords.push(...pageRecords);

        if (
          pageRecords.length === 0 ||
          pageRecords.length < pageSize
        ) {
          break;
        }

        page += 1;

        if (page > 100) {
          break;
        }
      }

      const uniqueOptions = Array.from(
        new Map(
          allRecords
            .map(recordToOption)
            .map((option) => [
              option.value,
              option,
            ])
        ).values()
      ).sort((first, second) =>
        first.text.localeCompare(second.text)
      );

      setOptions(uniqueOptions);
    } catch (error) {
      console.error(
        "Could not load Physical objects:",
        error
      );

      setLoadError(
        "The available Physical objects could not be loaded."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    loadPhysicalObjects();
  }, [loadPhysicalObjects]);


  useEffect(() => {
    if (isPhysicalObject && selectedValue) {
      formik.setFieldValue(
        fieldPath,
        "",
        false
      );

      formik.setFieldTouched(
        fieldPath,
        false,
        false
      );
    }
  }, [
    fieldPath,
    isPhysicalObject,
    selectedValue,
  ]);


  const validateValue = useCallback(
    (value) => {
      if (isPhysicalObject) {
        return undefined;
      }

      if (!value) {
        return "You must select a Heritage object.";
      }

      try {
        const parsedUrl = new URL(value);

        if (
          parsedUrl.protocol !== "http:" &&
          parsedUrl.protocol !== "https:"
        ) {
          return "You must select a valid Heritage object.";
        }
      } catch {
        return "You must select a valid Heritage object.";
      }

      return undefined;
    },
    [isPhysicalObject]
  );


  const dropdownOptions = useMemo(() => {
    if (
      !selectedValue ||
      options.some(
        (option) =>
          option.value === selectedValue
      )
    ) {
      return options;
    }

    return [
      {
        key: selectedValue,
        value: selectedValue,
        text: selectedValue,
      },
      ...options,
    ];
  }, [options, selectedValue]);


  if (isPhysicalObject) {
    return null;
  }

  return (
    <Field
      name={fieldPath}
      validate={validateValue}
    >
      {({ field, form, meta }) => {
        const hasError =
          Boolean(meta.touched && meta.error);

        return (
          <Form.Field
            required
            error={hasError}
          >
            <FieldLabel
              htmlFor={fieldPath}
              icon={icon}
              label={label}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <Dropdown
                id={fieldPath}
                name={fieldPath}
                fluid
                selection
                search
                clearable
                loading={isLoading}
                disabled={isLoading}
                placeholder={placeholder}
                noResultsMessage="No Physical objects found."
                options={dropdownOptions}
                value={field.value || ""}
                onChange={(_, data) => {
                  form.setFieldValue(
                    fieldPath,
                    data.value || "",
                    true
                  );

                  form.setFieldTouched(
                    fieldPath,
                    true,
                    false
                  );
                }}
                onBlur={() => {
                  form.setFieldTouched(
                    fieldPath,
                    true,
                    true
                  );
                }}
              />

              {field.value && (
                <Button
                  as="a"
                  type="button"
                  href={field.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon
                  labelPosition="left"
                >
                  <Icon name="external alternate" />
                  {openButtonLabel}
                </Button>
              )}
            </div>

            {description && (
              <label className="helptext">
                {description}
              </label>
            )}

            {hasError && (
              <Label pointing prompt>
                {meta.error}
              </Label>
            )}

            {loadError && (
              <Message
                negative
                size="small"
                content={loadError}
              />
            )}
          </Form.Field>
        );
      }}
    </Field>
  );
};


HeritageObjectField.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  label: PropTypes.string,
  icon: PropTypes.string,
  placeholder: PropTypes.string,
  description: PropTypes.string,
  openButtonLabel: PropTypes.string,
};


HeritageObjectField.defaultProps = {
  label: "Heritage object",
  icon: "archive",
  placeholder:
    "Select a Physical object...",
  description:
    "Select the Physical object associated with this record.",
  openButtonLabel:
    "Open physical object",
};


export default HeritageObjectField;
